import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/hooks/use-app-context';
import { validationGateApi } from '@/api/validation-gate.api';
import { canAccessModeratorWorkspace, isAdminRole } from '@/lib/auth/roles';
import type { ValidationGate } from '@/types/auth.types';
import { VALIDATION_GATE_QUERY_KEY } from './constants';

export function useValidationGate() {
  const { user } = useApp();
  const queryClient = useQueryClient();

  const isExempt =
    Boolean(user?.validationGate?.exempt) ||
    canAccessModeratorWorkspace(user?.role) ||
    isAdminRole(user?.role);

  const query = useQuery({
    queryKey: VALIDATION_GATE_QUERY_KEY,
    queryFn: validationGateApi.getMine,
    enabled: Boolean(user?.id) && !isExempt,
    staleTime: 30_000,
    initialData: user?.validationGate,
  });

  const gate = isExempt ? undefined : query.data;

  const setGateFromError = useCallback(
    (nextGate: ValidationGate) => {
      queryClient.setQueryData(VALIDATION_GATE_QUERY_KEY, nextGate);
    },
    [queryClient],
  );

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: VALIDATION_GATE_QUERY_KEY }),
    [queryClient],
  );

  const isGateEnabled = !isExempt && Boolean(gate);
  const isBlocked = isGateEnabled && gate!.isBlocked === true;
  const contributionsDone = gate?.contributionsSinceLastValidation ?? 0;
  const blockThreshold = gate?.blockThreshold ?? 50;
  const warningThreshold = gate?.warningThreshold ?? 40;
  const validationsCompleted = gate?.validationsSinceBlock ?? 0;
  const validationsNeeded = gate?.validationsRequired ?? 100;
  const remainingBeforeBlock = Math.max(0, blockThreshold - contributionsDone);
  const validationsRemaining = Math.max(0, validationsNeeded - validationsCompleted);
  const progressPercent = validationsNeeded > 0
    ? Math.min(100, Math.round((validationsCompleted / validationsNeeded) * 100))
    : 0;
  const isWarning = isGateEnabled && !isBlocked && contributionsDone >= warningThreshold;

  return {
    isGateEnabled,
    isExempt,
    isBlocked,
    isWarning,
    contributionsDone,
    blockThreshold,
    remainingBeforeBlock,
    validationsCompleted,
    validationsNeeded,
    validationsRemaining,
    progressPercent,
    gate,
    setGateFromError,
    invalidate,
    refetch: query.refetch,
  };
}
